import { useState, useCallback } from 'react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import type { MortgageData } from '@/types/calculator';
import type {
  CalculatorReportEmailData,
  RecipientInfoEmail,
  SenderInfoEmail,
  EnhancedAnalyticsEmail,
  EnhancedSupremeIntelligenceEmail,
} from '../types/calculator-email.types';
import type { BuydownScenario } from '@/components/calculator/results/utils/buydownCalculations';
import type { EnhancedUnifiedFormData } from '@/validation/enhancedUnifiedSchemas';
import { calculateBuydownAnalytics, calculateBuilderIncentiveImpact } from '@/utils/analytics/buydownAnalytics';
import { projectLongTermFinancials } from '@/utils/analytics/longTermProjections';
import { identifyOptimizationOpportunities } from '@/utils/analytics/optimizationEngine';
import { generateLoanProgramRecommendations } from '@/utils/supreme-intelligence/loanProgramScoring';
import { calculateFinancialHealthScore } from '@/utils/supreme-intelligence/financialHealthScore';

interface UseCalculatorEmailComposerOptions {
  mortgageData: MortgageData;
  buydownScenarios?: BuydownScenario[];
  formData?: EnhancedUnifiedFormData;
}

export const useCalculatorEmailComposer = ({
  mortgageData,
  buydownScenarios = [],
  formData,
}: UseCalculatorEmailComposerOptions) => {
  const { toast } = useToast();
  const [isSending, setIsSending] = useState(false);
  const [recipient, setRecipient] = useState<RecipientInfoEmail>({
    name: '',
    email: '',
  });
  const [sender, setSender] = useState<SenderInfoEmail>({
    name: '',
    email: '',
    phone: '',
    company: '',
    nmls: '',
  });
  const [customMessage, setCustomMessage] = useState('');

  const validateFields = useCallback((): string[] => {
    const errors: string[] = [];

    if (!recipient.name.trim()) {
      errors.push('Recipient name is required');
    }
    if (!recipient.email.trim()) {
      errors.push('Recipient email is required');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(recipient.email)) {
      errors.push('Please enter a valid email address');
    }
    if (!sender.name.trim()) {
      errors.push('Your name is required');
    }
    if (!sender.email.trim()) {
      errors.push('Your email is required');
    }

    return errors;
  }, [recipient, sender]);

  const buildEnhancedAnalytics = useCallback((): EnhancedAnalyticsEmail => {
    const loanAmount = mortgageData.homePrice - mortgageData.downPayment;
    const ltvRatio = (loanAmount / mortgageData.homePrice) * 100;
    const monthlyPayment = mortgageData.monthlyPayment.total;
    const annualPayment = monthlyPayment * 12;
    const totalCostOfOwnership = annualPayment * mortgageData.loanTerm;
    const requiredIncome = annualPayment / 0.28;
    const dtiRatio = 35; // Placeholder
    
    const riskLevel = ltvRatio > 95 ? 'High' : ltvRatio > 80 ? 'Medium' : 'Low';

    const baseAnalytics: EnhancedAnalyticsEmail = {
      riskLevel,
      ltvRatio,
      dtiRatio,
      totalCostOfOwnership,
      requiredIncome,
      paymentToIncomeRatio: 28,
    };

    // Add buydown analytics if scenarios available
    if (buydownScenarios.length > 0) {
      const buydownAnalytics = calculateBuydownAnalytics(buydownScenarios);
      baseAnalytics.buydownAnalytics = {
        scenarios: buydownAnalytics.scenarios.map(s => ({
          scenarioType: s.scenarioType,
          name: s.scenarioType === 'fixed' ? 'Fixed Rate' : `${s.scenarioType} Buydown`,
          netBenefit: s.netBenefit,
          totalSavings: s.totalSavings,
          breakEvenMonths: s.breakEvenMonths,
          monthlyPayment: s.monthlyPayment,
          cashToClose: s.cashToClose,
          isRecommended: s.isRecommended,
        })),
        bestScenario: buydownAnalytics.bestScenario,
        optimalStrategy: buydownAnalytics.optimalStrategy,
        totalSavingsRange: buydownAnalytics.totalSavingsRange,
      };

      const builderIncentive = calculateBuilderIncentiveImpact(buydownScenarios, mortgageData.closingCosts.total);
      baseAnalytics.builderIncentive = {
        totalBuilderCredit: builderIncentive.totalBuilderCredit,
        builderCreditPercentage: builderIncentive.builderCreditPercentage,
        effectivenessRating: builderIncentive.effectivenessRating,
        cashToCloseReduction: builderIncentive.cashToCloseReduction,
        comparisonWithoutCredits: builderIncentive.comparisonWithoutCredits,
      };
    }

    // Add long-term projections
    const longTermProjection = projectLongTermFinancials(mortgageData);
    baseAnalytics.longTermProjection = {
      milestones: longTermProjection.milestones,
      appreciationRate: longTermProjection.appreciationRate,
      netWorthImpact: longTermProjection.netWorthImpact,
    };

    // Add optimization opportunities
    const opportunities = identifyOptimizationOpportunities(mortgageData, buydownScenarios);
    baseAnalytics.optimizationOpportunities = opportunities.map(o => ({
      id: o.id,
      title: o.title,
      description: o.description,
      potentialSavings: o.potentialSavings,
      priority: o.priority,
      difficulty: o.implementationDifficulty,
      actionItems: o.actionItems,
    }));

    return baseAnalytics;
  }, [mortgageData, buydownScenarios]);

  const buildEnhancedSupremeIntelligence = useCallback((): EnhancedSupremeIntelligenceEmail | undefined => {
    if (!formData) return undefined;

    const monthlyPayment = mortgageData.monthlyPayment.total;
    const loanPrograms = generateLoanProgramRecommendations(formData, monthlyPayment);
    const financialHealth = calculateFinancialHealthScore(formData, monthlyPayment);
    
    const bestProgram = loanPrograms[0];
    const potentialSavings = bestProgram ? 
      Math.max(0, (monthlyPayment - bestProgram.monthlyPayment) * 12 * 5) : 0;

    return {
      recommendedLoanProgram: bestProgram?.programType || 'conventional',
      potentialSavings,
      optimizationSuggestions: financialHealth.improvements.slice(0, 3),
      financialHealthScore: financialHealth.totalScore,
      loanPrograms: loanPrograms.map(p => ({
        programType: p.programType,
        confidenceScore: p.confidenceScore,
        monthlyPayment: p.monthlyPayment,
        downPaymentRequired: p.downPaymentRequired,
        interestRate: p.interestRate,
        totalCostOverLife: p.totalCostOverLife,
        pros: p.pros,
        cons: p.cons,
        requirements: p.requirements,
      })),
      financialHealth: {
        totalScore: financialHealth.totalScore,
        grade: financialHealth.grade,
        color: financialHealth.color,
        components: Object.fromEntries(
          Object.entries(financialHealth.components).map(([key, comp]) => [
            key,
            { label: comp.label, score: comp.score, value: comp.value, weight: comp.weight }
          ])
        ),
        strengths: financialHealth.strengths,
        improvements: financialHealth.improvements,
        actionItems: financialHealth.actionItems.map(a => ({
          priority: a.priority,
          action: a.action,
          impact: a.impact,
          timeframe: a.timeframe,
        })),
        projectedScore12Months: financialHealth.projectedScore12Months,
      },
    };
  }, [formData, mortgageData.monthlyPayment.total]);

  const transformToEmailData = useCallback((): CalculatorReportEmailData => {
    const loanAmount = mortgageData.homePrice - mortgageData.downPayment;

    return {
      recipient,
      sender,
      mortgageSummary: {
        homePrice: mortgageData.homePrice,
        loanAmount,
        downPayment: mortgageData.downPayment,
        downPaymentPercentage: mortgageData.downPaymentPercentage,
        interestRate: mortgageData.interestRate,
        loanTerm: mortgageData.loanTerm,
        loanType: mortgageData.loanType,
        propertyType: mortgageData.propertyType,
        location: mortgageData.location,
        creditScore: mortgageData.creditScore,
      },
      monthlyPayment: {
        principal: mortgageData.monthlyPayment.principal,
        interest: mortgageData.monthlyPayment.interest,
        taxes: mortgageData.monthlyPayment.taxes,
        insurance: mortgageData.monthlyPayment.insurance,
        pmi: mortgageData.monthlyPayment.pmi,
        hoa: mortgageData.monthlyPayment.hoa,
        total: mortgageData.monthlyPayment.total,
      },
      closingCosts: {
        loanOrigination: mortgageData.closingCosts.loanOrigination,
        appraisalFee: mortgageData.closingCosts.appraisalFee,
        titleServices: mortgageData.closingCosts.titleServices,
        governmentRecordingCharges: mortgageData.closingCosts.governmentRecordingCharges,
        prepaids: mortgageData.closingCosts.prepaids,
        initialEscrow: mortgageData.closingCosts.initialEscrow,
        other: mortgageData.closingCosts.other,
        fundingFee: mortgageData.closingCosts.fundingFee,
        sellerCreditAmount: mortgageData.closingCosts.sellerCreditAmount,
        total: mortgageData.closingCosts.total,
      },
      analytics: buildEnhancedAnalytics(),
      supremeIntelligence: buildEnhancedSupremeIntelligence(),
      customMessage: customMessage.trim() || undefined,
    };
  }, [mortgageData, recipient, sender, customMessage, buildEnhancedAnalytics, buildEnhancedSupremeIntelligence]);

  const sendEmail = useCallback(async (): Promise<boolean> => {
    const errors = validateFields();
    if (errors.length > 0) {
      toast({
        title: 'Validation Error',
        description: errors[0],
        variant: 'destructive',
      });
      return false;
    }

    setIsSending(true);

    try {
      const emailData = transformToEmailData();

      const { data, error } = await supabase.functions.invoke(
        'send-calculator-report-email',
        { body: emailData }
      );

      if (error) {
        throw error;
      }

      toast({
        title: 'Email Sent!',
        description: `Calculator report sent to ${recipient.email}`,
      });

      return true;
    } catch (error: any) {
      console.error('Failed to send calculator report email:', error);
      toast({
        title: 'Failed to Send Email',
        description: error.message || 'An unexpected error occurred',
        variant: 'destructive',
      });
      return false;
    } finally {
      setIsSending(false);
    }
  }, [validateFields, transformToEmailData, recipient.email, toast]);

  const resetForm = useCallback(() => {
    setRecipient({ name: '', email: '' });
    setCustomMessage('');
  }, []);

  const loadFromHistory = useCallback(
    (data: { recipient: RecipientInfoEmail; sender: SenderInfoEmail }) => {
      setRecipient(data.recipient);
      setSender(data.sender);
    },
    []
  );

  return {
    recipient,
    setRecipient,
    sender,
    setSender,
    customMessage,
    setCustomMessage,
    isSending,
    sendEmail,
    validateFields,
    resetForm,
    loadFromHistory,
  };
};
